import { CommonModule } from '@angular/common';
import { Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TenantUsersDto } from '@buspro/web/apis';
import { ParcelDeliveryOrderDto, ParcelDeliveryOrdersApi, ParcelItemDto } from '@buspro/web/apis';
import { DEFAULT_COMPANY_LOGO, QrCodeConfigService } from '@buspro/web/core';
import { NgxAuthService } from '@corelabs/ng-auth';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { QRCodeModule } from 'angularx-qrcode';
import { NgxPrinterModule } from 'ngx-printer';
import { Observable, lastValueFrom } from 'rxjs';
import { PrintService } from './print.service';

@Component({
  selector: 'tenant-parcel-sticker-print-sheet',
  standalone: true,
  imports: [CommonModule, NgxPrinterModule, QRCodeModule],
  template: `
    <div class="invoice bg-gray-100">
      <div class="print-button-container">
        <button
          class="btn btn-primary btn-lg shadow-lg"
          (click)="printService.onDataReady()"
        >
          <span class="fe fe-printer"></span> Print Sticker
        </button>
      </div>

      <div *ngIf="parcelQuery.isFetched()">
        <div
          id="printableSticker"
          [ngStyle]="{ width: '102mm', height: '84mm' }"
          *ngFor="let item of parcel?.items; let i = index"
          class="card bg-white mx-auto mb-8 p-3 border-2 border-dashed border-gray-300 rounded-lg"
        >
          <div class="flex justify-between items-center mb-2">
            <img
              [src]="user.company.logoUrl || companyLogoUrl"
              alt="Swift Transit Logo"
              class="h-10"
            />
            <div class="text-right text-xs">
              <p class="font-bold text-lg">{{ user.company.name }}</p>
              <p *ngIf="user.company.phoneNumber">
                Tel: {{ user.company.phoneNumber || 'N/A' }}
              </p>
            </div>
          </div>

          <div class="text-center mb-2">
            <h3 class="text-md font-bold">Parcel Item Sticker</h3>
          </div>

          <div class="grid grid-cols-4 gap-0 text-xs mb-2">
            <div>
              <p><strong>Parcel No:</strong></p>
              <p><strong>Item:</strong></p>
              <p><strong>From - To:</strong></p>
            </div>
            <div class="col-span-3">
              <p>{{ parcel!.items[i].parcelNo }}</p>
              <p>
                {{ parcel!.items[i].description || '' }} ({{
                  parcel!.items[i].parcelTypeName
                }})
              </p>
              <p>
                {{ parcel!.deliverySubRouteName }}

              </p>
            </div>
          </div>

          <!--      <div class="text-xs mb-2">-->
          <!--        <p><strong>Description:</strong> {{ selected.items[0].description }}</p>-->
          <!--      </div>-->

          <div class="flex justify-between items-center">
            <qrcode
              [qrdata]="getItemQrCodeValue(parcel!.items[0])"
              [width]="75"
              [errorCorrectionLevel]="'Q'"
            ></qrcode>
            <div class="text-right text-xs">
              <p>Scan to track</p>
              <p *ngIf="user.company?.websiteUrl">
                www.{{ user.company.websiteUrl || 'N/A' }}
              </p>
              <p>
                <strong>Item {{ i + 1 }} of {{ parcel.items.length ||0 }}</strong>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  styles: `

    .invoice {
      page-break-after: always;
    }

      .invoice:last-child {
        page-break-after: avoid;
      }

      .print-button-container {
        position: fixed;
        top: 20px;
        right: 20px;
        z-index: 1000;
      }

      .btn-lg {
        padding: 0.75rem 1.5rem;
        font-size: 1.25rem;
      }

@media print {
  .print-button-container {
    display: none;
  }

  #printableSticker {
    border: none !important;
    width: 102mm;
    height: 84mm;
  }

  @page {
    size: 102mm 84mm;
    margin: 0;
  }
}
  `,
})
export class ParcelStickerPrintSheetComponent {
  private parcelDeliveryOrderApi = inject(ParcelDeliveryOrdersApi);
  private ngxAuthService = inject(NgxAuthService);
  printService = inject(PrintService);
  private qrCodeConfigService = inject(QrCodeConfigService);

  parcelId = signal<string>('');

  authUser$: Observable<TenantUsersDto | null> = this.ngxAuthService.state;
  userSignal = toSignal(this.authUser$, { initialValue: null });

  parcelQuery = injectQuery(() => {
    return {
      queryKey: ['parcel-sticker-to-print', this.parcelId()],
      queryFn: () =>
        lastValueFrom(
          this.parcelDeliveryOrderApi.parcelDeliveryOrderControllerGetSingleParcelById({
            id: this.parcelId(),
          }),
        ),
      select: (data: ParcelDeliveryOrderDto) => {
        console.log('parcel to print', data);
        if (data) {
          this.printService.onDataReady();
          setTimeout(() => {
            if (this.printService.isPrinting()) {
              console.log('Print dialog may not have opened. Please use the print button.');
            }
          }, 2000);
        }
        return data;
      },
      enabled: !!this.parcelId(),
    };
  });

  get user() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.userSignal()!;
  }

  get parcel() {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.parcelQuery.data()!;
  }

  constructor(route: ActivatedRoute) {
    this.parcelId.set(route.snapshot.params['parcelId']);
  }

  calculateGrandTotal(items: ParcelItemDto[]): number {
    return items.reduce((total, item) => total + (item.totalCost || 0), 0);
  }

  getQrCodeValue(parcelDeliveryOrder: ParcelDeliveryOrderDto): string {
    return this.qrCodeConfigService.generateDeliveryOrderTrackingUrl(parcelDeliveryOrder.id);
  }

  getItemQrCodeValue(item: ParcelItemDto): string {
    return this.qrCodeConfigService.generateParcelTrackingUrl(item.deliveryOrderId, item.id);
  }

  protected readonly companyLogoUrl = DEFAULT_COMPANY_LOGO;
}
